import { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Pencil, Plus, Power, Trash2, Archive } from "lucide-react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useCommunity } from "@/context/CommunityContext";
import ImageUpload, { ImageGalleryUpload } from "@/components/ImageUpload";
import ModulesEditor from "@/admin/ModulesEditor";
import { SCHEMAS } from "@/admin/schemas";

function Field({ field, value, onChange, refs }) {
  const common = { "data-testid": `field-${field.name}` };
  if (field.type === "textarea")
    return <textarea className="field min-h-[110px]" value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...common} />;
  if (field.type === "boolean")
    return (
      <label className="inline-flex items-center gap-2 text-sm">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} {...common} />
        {value ? "ja" : "nein"}
      </label>
    );
  if (field.type === "color")
    return (
      <div className="flex items-center gap-2">
        <input type="color" value={value || "#2F4A3C"} onChange={(e) => onChange(e.target.value)} className="h-10 w-14 rounded-lg border" {...common} />
        <input className="field" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  if (field.type === "select")
    return (
      <select className="field" value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...common}>
        <option value="">— bitte wählen —</option>
        {field.options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    );
  if (field.type === "ref")
    return (
      <select className="field" value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} {...common}>
        <option value="">— keine Auswahl —</option>
        {(refs[field.resource] || []).map((item) => (
          <option key={item.id} value={item.id}>{item.title || item.name}</option>
        ))}
      </select>
    );
  if (field.type === "image")
    return <ImageUpload value={value} onChange={onChange} label="" testId={`field-${field.name}`} accept={field.accept || "image/*"} />;
  if (field.type === "gallery")
    return <ImageGalleryUpload value={value} onChange={onChange} testId={`field-${field.name}`} accept={field.accept || "image/*"} />;
  if (field.type === "modules")
    return <ModulesEditor value={value} onChange={onChange} discoveryItems={refs.discovery_items || []} />;
  return <input className="field" value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...common} />;
}

export default function ResourcePage({ resource }) {
  const schema = SCHEMAS[resource];
  const { community } = useCommunity();
  const [items, setItems] = useState([]);
  const [refs, setRefs] = useState({});
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [walkFilter, setWalkFilter] = useState("");

  const titleField = schema.titleField || "title";
  const scopeParams = useMemo(
    () => (schema.scoped && community ? { community_id: community.id } : {}),
    [schema.scoped, community]
  );

  const load = useCallback(() => {
    api
      .get(`/admin/${resource}`, { params: { ...scopeParams, include_archived: showArchived } })
      .then(({ data }) => setItems(data || []))
      .catch((e) => setError(errMsg(e)));
  }, [resource, scopeParams, showArchived]);

  useEffect(load, [load]);

  useEffect(() => {
    const needed = new Set(schema.fields.filter((f) => f.type === "ref").map((f) => f.resource));
    if (schema.fields.some((f) => f.type === "modules")) needed.add("discovery_items");
    if (schema.filterBy === "walk_id") needed.add("walks");
    Promise.all(
      [...needed].map((name) =>
        api.get(`/admin/${name}`, { params: scopeParams }).then(({ data }) => [name, data || []]).catch(() => [name, []])
      )
    ).then((entries) => setRefs(Object.fromEntries(entries)));
  }, [resource, schema, scopeParams]);

  const startCreate = () => {
    const defaults = {};
    schema.fields.forEach((field) => {
      if (field.defaultValue !== undefined) defaults[field.name] = field.defaultValue;
      else if (field.type === "boolean") defaults[field.name] = field.name === "active" || field.name === "enabled";
      else if (field.type === "modules" || field.type === "gallery") defaults[field.name] = [];
      else defaults[field.name] = "";
    });
    if (schema.filterBy === "walk_id" && walkFilter) defaults.walk_id = walkFilter;
    setForm(defaults);
    setEditing("new");
    setError("");
    setNotice("");
  };

  const startEdit = (item) => {
    const next = {};
    schema.fields.forEach((field) => {
      next[field.name] = item[field.name] ?? field.defaultValue ?? (["modules", "gallery"].includes(field.type) ? [] : field.type === "boolean" ? false : "");
    });
    setForm(next);
    setEditing(item.id);
    setError("");
    setNotice("");
  };

  const save = async (event) => {
    event.preventDefault();
    setError("");
    const missing = schema.fields.find((f) => f.required && !String(form[f.name] ?? "").trim());
    if (missing) {
      setError(`Bitte ${missing.label} ausfüllen.`);
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, ...(schema.scoped && community ? { community_id: community.id } : {}) };
      if (editing === "new") await api.post(`/admin/${resource}`, payload);
      else await api.put(`/admin/${resource}/${editing}`, payload);
      setNotice("Gespeichert.");
      setEditing(null);
      load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const action = async (method, path, params) => {
    setError("");
    try {
      await api[method](`/admin/${resource}${path}`, method === "post" ? params : { params });
      load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const remove = async (item) => {
    setError("");
    try {
      await api.delete(`/admin/${resource}/${item.id}`);
      load();
    } catch (e) {
      setError(errMsg(e));
    }
  };

  const visible = schema.filterBy === "walk_id" && walkFilter ? items.filter((i) => i.walk_id === walkFilter) : items;

  return (
    <div data-testid={`admin-${resource}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl">{schema.label}</h1>
          {schema.scoped ? (
            <p className="text-sm text-[var(--ink-soft)]">Gemeinde: {community?.name || "keine ausgewählt"}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {schema.filterBy === "walk_id" ? (
            <select className="field !w-auto" value={walkFilter} onChange={(e) => setWalkFilter(e.target.value)} data-testid="walk-filter">
              <option value="">Alle Wege</option>
              {(refs.walks || []).map((walk) => (
                <option key={walk.id} value={walk.id}>{walk.title}</option>
              ))}
            </select>
          ) : null}
          <label className="inline-flex items-center gap-2 text-xs text-[var(--ink-soft)]">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="show-archived" />
            Archivierte anzeigen
          </label>
          <button className="btn btn-primary !py-2.5 !text-xs" onClick={startCreate} data-testid="resource-create">
            <Plus className="h-4 w-4" /> NEU ANLEGEN
          </button>
        </div>
      </div>

      {error ? <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="resource-error">{error}</p> : null}
      {notice ? <p className="mt-4 rounded-xl bg-[var(--brand-soft)] px-4 py-3 text-sm" data-testid="resource-notice">{notice}</p> : null}

      {editing ? (
        <form onSubmit={save} className="mt-6 rounded-2xl border bg-white p-6" data-testid="resource-form">
          <p className="eyebrow">{editing === "new" ? "Neu anlegen" : "Bearbeiten"}</p>
          <div className="mt-4 grid gap-5 md:grid-cols-2">
            {schema.fields
              .filter((field) => !field.showFor || field.showFor.includes(form[schema.fields[0].name]) || field.showFor.includes(form.challenge_type) || field.showFor.includes(form.game_type))
              .map((field) => (
                <div key={field.name} className={["textarea", "modules", "gallery"].includes(field.type) ? "md:col-span-2" : ""}>
                  <div className="eyebrow mb-1">{field.label}{field.required ? " *" : ""}</div>
                  <Field field={field} value={form[field.name]} onChange={(v) => setForm((f) => ({ ...f, [field.name]: v }))} refs={refs} />
                </div>
              ))}
          </div>
          <div className="mt-6 flex gap-2">
            <button className="btn btn-primary" disabled={busy} data-testid="resource-save">SPEICHERN</button>
            <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)} data-testid="resource-cancel">ABBRECHEN</button>
          </div>
        </form>
      ) : null}

      <div className="mt-6 overflow-hidden rounded-2xl border bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-[var(--brand-soft)] text-left text-xs uppercase tracking-wide">
              <th className="px-4 py-3">Titel</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td className="px-4 py-6 text-[var(--ink-soft)]" colSpan={3}>Noch keine Einträge.</td></tr>
            ) : null}
            {visible.map((item) => (
              <tr key={item.id} className="border-b last:border-0" data-testid={`row-${item.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {item.image || item.profile_image || item.hero_image ? (
                      <img
                        src={fileUrl(item.image || item.profile_image || item.hero_image)}
                        alt=""
                        className="h-14 w-14 shrink-0 rounded-xl border object-cover"
                        loading="lazy"
                      />
                    ) : null}
                    <div className="min-w-0">
                      {item.inventory_number ? <p className="eyebrow">{item.inventory_number}</p> : null}
                      <p className="font-semibold">{item[titleField] || item.title || item.name}</p>
                      {item.subtitle ? <p className="text-xs text-[var(--ink-soft)]">{item.subtitle}</p> : null}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-[var(--ink-soft)]">
                  {item.archived ? "archiviert" : item.active === false ? "inaktiv" : "aktiv"}
                  {item.status ? ` · ${item.status}` : ""}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap justify-end gap-1">
                    <button className="btn btn-ghost !px-2.5 !py-1.5 !text-xs" onClick={() => startEdit(item)} data-testid={`edit-${item.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    <button className="btn btn-ghost !px-2.5 !py-1.5 !text-xs" onClick={() => action("post", `/${item.id}/duplicate`, {})} data-testid={`duplicate-${item.id}`}><Copy className="h-3.5 w-3.5" /></button>
                    <button className="btn btn-ghost !px-2.5 !py-1.5 !text-xs" onClick={() => action("post", `/${item.id}/set-active`, { active: item.active === false })} data-testid={`toggle-${item.id}`}><Power className="h-3.5 w-3.5" /></button>
                    <button className="btn btn-ghost !px-2.5 !py-1.5 !text-xs" onClick={() => action("post", `/${item.id}/archive`, { archived: !item.archived })} data-testid={`archive-${item.id}`}><Archive className="h-3.5 w-3.5" /></button>
                    <button className="btn btn-ghost !px-2.5 !py-1.5 !text-xs" onClick={() => remove(item)} data-testid={`delete-${item.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
